/**
 * Launch context — the query params the app was started with.
 *
 * Onboarding strips query params from the URL after the cold-start redirect
 * (the rules engine routes from durable state, not the URL — see app/index.tsx),
 * so a launch param like `?vida_client=…` is gone by the time most code runs.
 * We capture the launch query string ONCE, at module load, and freeze it so
 * every later consumer sees the original launch params regardless of the URL at
 * the time they read.
 *
 * This is the generic seam: `getNativeAppClientInfo` derives the native-app /
 * iframe flags from it today; future families (e.g. `utm_*` attribution) read
 * their params through `getLaunchParam` the same way.
 */

// Captured eagerly at module load — the earliest point JS runs (during initial
// bundle evaluation), before React renders a single provider or any redirect can
// strip the query string. No dependence on mount/effect ordering. `window.location`
// only exists on web (incl. the react-native-web / native-webview bundles where
// the embed params actually arrive); native RN has no `window.location`, so launch
// params are empty there.
const launchSearch = typeof window !== 'undefined' ? (window.location?.search ?? '') : '';
// Guard `URLSearchParams` itself: it isn't guaranteed on every native JS runtime
// (some Hermes/JSC builds lack it without a polyfill), and this runs at module
// load — an unguarded `new URLSearchParams(...)` would throw and crash the bundle
// on import. When unavailable, launch params are simply empty (native isn't embedded).
const launchParams =
  typeof URLSearchParams !== 'undefined' ? new URLSearchParams(launchSearch) : null;

/**
 * Read a single launch param by its raw query key (e.g. 'vida_client',
 * 'utm_source'). Returns `undefined` when absent. The value is whatever the URL
 * carried — callers normalize (lowercase / parse) as needed.
 */
export const getLaunchParam = (key: string): string | undefined =>
  launchParams?.get(key) ?? undefined;
